import { Badge, Flex, Icon, Table, Text } from '@chakra-ui/react';
import { memo } from 'react';
import { LuUser } from 'react-icons/lu';

import { ContractSeller, ContractStatus } from 'helpers/fakeData';

const statusConfig: Record<ContractStatus, { label: string; color: string }> = {
  ACTIVE: {
    label: 'Active',
    color: 'green.500',
  },
  PENDING: {
    label: 'Pending',
    color: 'yellow.500',
  },
  EXPIRED: {
    label: 'Expired',
    color: 'red.500',
  },
};

const ContractStatusBadge = ({ status }: { status: ContractStatus }) => {
  const config = statusConfig[status];
  return (
    <Badge>
      <Text fontSize="xs" fontWeight="semibold" color={config.color}>
        {config.label}
      </Text>
    </Badge>
  );
};

type Column = {
  key: string;
  label: string;
  icon?: React.ReactNode;
};
const COLUMNS: Column[] = [
  { key: 'name', label: 'Name', icon: <LuUser /> },
  { key: 'quantity', label: 'Quantity' },
  { key: 'contribution', label: 'Allocation' },
  { key: 'status', label: 'Status' },
] as const;

interface Props {
  counterparties: ContractSeller[];
}

const CounterpartyTable = ({ counterparties }: Props) => {
  return (
    <Table.Root
      size="sm"
      tableLayout="fixed"
      borderWidth="1px"
      borderColor="border.light"
      borderRadius="lg"
      overflow="hidden"
      background="surface.background"
    >
      <Table.Header>
        <Table.Row bg="transparent">
          {COLUMNS.map((col) => (
            <Table.ColumnHeader
              key={col.key}
              px={4}
              py={3}
              fontSize="sm"
              fontWeight="semibold"
              borderColor="border.light"
              maxW="sm"
            >
              <Flex direction="row" gap={2} alignItems="center">
                {col.icon != null && (
                  <Icon boxSize="14px" color="text.secondary">
                    {col.icon}
                  </Icon>
                )}
                <Text>{col.label}</Text>
              </Flex>
            </Table.ColumnHeader>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {counterparties.map((counterparty) => {
          return (
            <Table.Row key={counterparty.id} bg="transparent" fontWeight="medium">
              <Table.Cell border="none" px={4} py={3}>
                <Text fontSize="sm" truncate maxLines={1}>
                  {counterparty.name}
                </Text>
              </Table.Cell>
              <Table.Cell border="none" px={4} py={3}>
                <Text fontSize="sm">{counterparty.quantity}</Text>
              </Table.Cell>
              <Table.Cell border="none" px={4} py={3}>
                <Text fontSize="sm">{counterparty.contribution}</Text>
              </Table.Cell>
              <Table.Cell border="none" px={4} py={3}>
                <ContractStatusBadge status={counterparty.status} />
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table.Root>
  );
};

export default memo(CounterpartyTable);
